import { type BookingSite } from '@quoruum/frontend-common/oas/gen/enumValues.gen';
import {
  hotelGetOptions,
  locationGetOptions,
} from '@quoruum/frontend-common/oas/gen/quoruum/@tanstack/react-query.gen';
import type { HotelBean } from '@quoruum/frontend-common/oas/gen/quoruum/types.gen';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CitySection } from './CitySection';
import { CityStatusSection } from './CityStatusSection';
import { HotelSection } from './HotelSection';
import { HotelSettingsSection } from './HotelSettingsSection';
import { RatesSection } from './RatesSection';
import { ScrapeMetadataSection, useIsBootstrapLearner } from './ScrapeMetadataSection';
import { StatusSection } from './StatusSection';
import { DrawerTab, SelectedType } from './hotelMapShared';

function formatHotelAddress(hotel: HotelBean): string {
  const cityName = hotel.city?.name;
  const stateCode = hotel.city?.stateCode;
  const countryCode = hotel.city?.countryCode;
  const cityRegion = [cityName, stateCode].filter(Boolean).join(', ');

  return [hotel.address1, hotel.address2, cityRegion, countryCode, hotel.postalCode]
    .filter(Boolean)
    .join(', ');
}

function buildHotelSentryLogsLink(hotelId: string): string {
  const globalFilter = {
    dataset: 'logs',
    tag: {
      key: 'event.hotelId',
      name: 'event.hotelId',
      kind: 'tag',
    },
    value: `event.hotelId:${hotelId}`,
  };

  const searchParams = new URLSearchParams({
    globalFilter: JSON.stringify(globalFilter),
    release: '',
  });

  return `https://quoruum.sentry.io/dashboard/4107132/?${searchParams.toString()}`;
}

type CenterTabPanelProps = {
  selectedId: string;
  selectedType: SelectedType;
  activeDrawerTab: DrawerTab;
  setActiveDrawerTab: (tab: DrawerTab) => void;
  scrapeMetadataSite: BookingSite;
  setScrapeMetadataSite: (site: BookingSite) => void;
  onClose: () => void;
};

export function CenterTabPanel({
  selectedId,
  selectedType,
  ...props
}: CenterTabPanelProps): ReactElement {
  if (selectedType === 'CITY') {
    return (
      <CityTabPanel
        selectedId={selectedId}
        activeDrawerTab={props.activeDrawerTab}
        setActiveDrawerTab={props.setActiveDrawerTab}
      />
    );
  }

  return <HotelTabPanel selectedId={selectedId} {...props} />;
}

type HotelTabPanelProps = Omit<CenterTabPanelProps, 'selectedType'>;

function HotelTabPanel({
  selectedId,
  activeDrawerTab,
  setActiveDrawerTab,
  scrapeMetadataSite,
  setScrapeMetadataSite,
  onClose,
}: HotelTabPanelProps): ReactElement {
  const { data: selectedHotelByIdResponse } = useQuery({
    ...hotelGetOptions({
      path: {
        id: selectedId,
      },
    }),
    staleTime: 30 * 1000,
  });
  const hotelDetails =
    selectedHotelByIdResponse?.id === selectedId ? selectedHotelByIdResponse : null;

  const isSelectedHotelBootstrapLearner = useIsBootstrapLearner(selectedId, scrapeMetadataSite);
  const drawerPanelClassName = 'flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden';

  if (!hotelDetails) {
    return <></>;
  }

  return (
    <aside className={drawerPanelClassName}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900">
            {isSelectedHotelBootstrapLearner ? (
              <span title="Bootstrap Learner" aria-label="Bootstrap Learner">
                🥾
              </span>
            ) : null}
            {hotelDetails?.name ?? 'Hotel Details'}
            {hotelDetails?.brand?.name ? (
              hotelDetails.brand.id ? (
                <>
                  {' ('}
                  <Link
                    to={`/tools/companies?companyId=${encodeURIComponent(hotelDetails.brand.id)}`}
                    className="text-sky-700 underline"
                  >
                    {hotelDetails.brand.name}
                  </Link>
                  {')'}
                </>
              ) : (
                ` (${hotelDetails.brand.name})`
              )
            ) : (
              ''
            )}
            {` (${selectedId})`}
            <a
              href={buildHotelSentryLogsLink(selectedId)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-700 underline"
            >
              (Sentry)
            </a>
          </h3>
          <p className="mt-1 text-xs text-slate-600">
            {hotelDetails
              ? formatHotelAddress(hotelDetails) || 'Address unavailable'
              : 'Address unavailable'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-md px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
        >
          Close
        </button>
      </div>

      <div className="mb-3 flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          type="button"
          onClick={() => setActiveDrawerTab('HOTEL')}
          className={`rounded-md px-3 py-1 text-xs font-medium ${
            activeDrawerTab === 'HOTEL'
              ? 'bg-sky-100 text-sky-800'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Hotel
        </button>
        <button
          type="button"
          onClick={() => setActiveDrawerTab('HOTEL_SETTINGS')}
          className={`rounded-md px-3 py-1 text-xs font-medium ${
            activeDrawerTab === 'HOTEL_SETTINGS'
              ? 'bg-sky-100 text-sky-800'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Hotel Settings
        </button>
        <button
          type="button"
          onClick={() => setActiveDrawerTab('SCRAPE_METADATA')}
          className={`rounded-md px-3 py-1 text-xs font-medium ${
            activeDrawerTab === 'SCRAPE_METADATA'
              ? 'bg-sky-100 text-sky-800'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Scrape Metadata
        </button>
        <button
          type="button"
          onClick={() => setActiveDrawerTab('STATUS')}
          className={`rounded-md px-3 py-1 text-xs font-medium ${
            activeDrawerTab === 'STATUS'
              ? 'bg-sky-100 text-sky-800'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Status
        </button>
        <button
          type="button"
          onClick={() => setActiveDrawerTab('RATES')}
          className={`rounded-md px-3 py-1 text-xs font-medium ${
            activeDrawerTab === 'RATES'
              ? 'bg-sky-100 text-sky-800'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Rates
        </button>
      </div>

      {activeDrawerTab === 'HOTEL_SETTINGS' ? (
        <HotelSettingsSection selectedHotelId={selectedId} />
      ) : activeDrawerTab === 'HOTEL' ? (
        <HotelSection selectedHotelDetails={hotelDetails} />
      ) : activeDrawerTab === 'SCRAPE_METADATA' ? (
        <ScrapeMetadataSection
          selectedHotelId={selectedId}
          scrapeMetadataSite={scrapeMetadataSite}
          setScrapeMetadataSite={setScrapeMetadataSite}
        />
      ) : activeDrawerTab === 'STATUS' ? (
        <StatusSection selectedHotelId={selectedId} />
      ) : (
        <RatesSection selectedHotelDetails={hotelDetails} selectedHotelId={selectedId} />
      )}
    </aside>
  );
}

type CityTabPanelProps = {
  selectedId: string;
  activeDrawerTab: DrawerTab;
  setActiveDrawerTab: (tab: DrawerTab) => void;
};

function CityTabPanel({
  selectedId,
  activeDrawerTab,
  setActiveDrawerTab,
}: CityTabPanelProps): ReactElement {
  const [searchParams] = useSearchParams();
  const { data: locationResponse } = useQuery({
    ...locationGetOptions({ path: { id: selectedId } }),
    staleTime: 30 * 1000,
  });
  const selectedLocation = locationResponse?.id === selectedId ? locationResponse : null;
  const drawerPanelClassName = 'flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden';

  if (!selectedLocation) {
    return <></>;
  }

  return (
    <aside className={drawerPanelClassName}>
      <h3 className="mb-3 text-base font-semibold text-slate-900">City Details ({selectedId})</h3>
      <div className="mb-3 flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          type="button"
          onClick={() => setActiveDrawerTab('CITY')}
          className={`rounded-md px-3 py-1 text-xs font-medium ${
            activeDrawerTab === 'CITY'
              ? 'bg-sky-100 text-sky-800'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          City
        </button>
        <button
          type="button"
          onClick={() => setActiveDrawerTab('CITY_STATUS')}
          className={`rounded-md px-3 py-1 text-xs font-medium ${
            activeDrawerTab === 'CITY_STATUS'
              ? 'bg-sky-100 text-sky-800'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          City Status
        </button>
      </div>

      {activeDrawerTab === 'CITY' ? (
        <CitySection selectedCityId={selectedId} selectedLocationByIdResponse={selectedLocation} />
      ) : (
        <CityStatusSection selectedCityId={selectedId} searchParams={searchParams} />
      )}
    </aside>
  );
}
